import React, { useState } from "react";
import { Box, Drawer, IconButton, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import HotelIcon from "@mui/icons-material/Hotel";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SettingsIcon from "@mui/icons-material/Settings";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";

import { IPD_COLORS, IPD_SHAPE } from "../../ipd/utils";
import { IPD_SIDEBAR } from "../../ipd/utils/ipdSidebar";

/**
 * The Emergency Department module shell — a dark collapsible left sidebar that
 * every emergency screen renders inside, so the module reads as one contained
 * app rather than a set of unrelated pages under the hospital's top navbar.
 *
 * Desktop: a fixed-width pane that collapses to an icon rail. Below ~900px it
 * becomes an off-canvas drawer opened from a header hamburger. The dark chrome
 * uses the shared IPD_SIDEBAR palette, the same one the inventory suite uses.
 */

const NAV = [
  { to: "/emergency_tracking_board", label: "Tracking Board", icon: MonitorHeartIcon },
  { to: "/emergency_register_patient", label: "Register Patient", icon: PersonAddAlt1Icon },
  { to: "/emergency_all_visits", label: "All Visits", icon: FormatListBulletedIcon },
  { to: "/emergency_bed_management", label: "Bed Management", icon: HotelIcon },
  { to: "/emergency_death_register", label: "Death Register", icon: MenuBookIcon },
  { to: "/emergency_department_setup", label: "Department Setup", icon: SettingsIcon },
];

const EXPANDED = 248;
const COLLAPSED = 72;

function SidebarContent({ collapsed, pathname, onNavigate }) {
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        background: `linear-gradient(180deg, ${IPD_SIDEBAR.bg} 0%, ${IPD_SIDEBAR.bgGradientEnd} 100%)`,
        color: IPD_SIDEBAR.text,
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: collapsed ? 0 : 2.25,
          py: 2,
          justifyContent: collapsed ? "center" : "flex-start",
          borderBottom: `1px solid ${IPD_SIDEBAR.border}`,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: "9px",
            display: "grid",
            placeItems: "center",
            bgcolor: IPD_COLORS.primaryLight,
            color: "#fff",
          }}
        >
          <LocalHospitalIcon sx={{ fontSize: 20 }} />
        </Box>
        {!collapsed && (
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#fff", lineHeight: 1.2, whiteSpace: "nowrap" }}>
              Emergency
            </Typography>
            <Typography sx={{ fontSize: 11, color: IPD_SIDEBAR.label, whiteSpace: "nowrap" }}>
              Department
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        component="nav"
        sx={{
          flex: 1,
          py: 1.5,
          px: collapsed ? 1 : 1.25,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        {NAV.map((item) => {
          const active = pathname.startsWith(item.to);
          const Icon = item.icon;
          const button = (
            <Box
              component="button"
              type="button"
              onClick={() => onNavigate(item.to)}
              aria-current={active ? "page" : undefined}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                width: "100%",
                justifyContent: collapsed ? "center" : "flex-start",
                px: collapsed ? 0 : 1.5,
                py: 1.1,
                borderRadius: "9px",
                cursor: "pointer",
                border: "none",
                fontFamily: "inherit",
                textAlign: "left",
                color: active ? "#fff" : IPD_SIDEBAR.text,
                bgcolor: active ? IPD_SIDEBAR.active : "transparent",
                transition: "background .15s ease, color .15s ease",
                "&:hover": {
                  bgcolor: active ? IPD_SIDEBAR.active : IPD_SIDEBAR.hover,
                  color: "#fff",
                },
                "&:focus-visible": { outline: `2px solid ${IPD_COLORS.selectedBorder}`, outlineOffset: 2 },
              }}
            >
              <Icon sx={{ fontSize: 20, flexShrink: 0 }} />
              {!collapsed && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: 13.5,
                    fontWeight: active ? 700 : 500,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item.label}
                </Typography>
              )}
            </Box>
          );
          return collapsed ? (
            <Tooltip key={item.to} title={item.label} placement="right">
              {button}
            </Tooltip>
          ) : (
            <React.Fragment key={item.to}>{button}</React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

export default function EmergencyShell({ children }) {
  const history = useHistory();
  const location = useLocation();
  // Fixed pane only on large screens; below this the sidebar is a drawer so
  // content (e.g. the board's wide grid) keeps the full width on laptops.
  const isMobile = useMediaQuery("(max-width:1199px)");
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navigate = (to) => {
    history.push(to);
    setMobileOpen(false);
  };

  const width = collapsed ? COLLAPSED : EXPANDED;

  return (
    <Box sx={{ display: "flex", minHeight: "100%", bgcolor: IPD_COLORS.pageBg }}>
      {!isMobile && (
        <Box
          sx={{
            position: "sticky",
            top: 0,
            alignSelf: "flex-start",
            flexShrink: 0,
            width,
            height: "100dvh",
            transition: "width .2s ease",
          }}
        >
          <SidebarContent collapsed={collapsed} pathname={location.pathname} onNavigate={navigate} />
          <IconButton
            onClick={() => setCollapsed((c) => !c)}
            size="small"
            aria-label={collapsed ? "Expand the menu" : "Collapse the menu"}
            sx={{
              position: "absolute",
              top: 18,
              right: -13,
              zIndex: 2,
              width: 26,
              height: 26,
              bgcolor: IPD_COLORS.cardBg,
              border: `1px solid ${IPD_COLORS.border}`,
              boxShadow: IPD_COLORS.shadow.card,
              color: IPD_COLORS.textSecondary,
              "&:hover": { bgcolor: IPD_COLORS.cardBg, color: IPD_COLORS.primary },
            }}
          >
            {collapsed ? <ChevronRightIcon sx={{ fontSize: 17 }} /> : <ChevronLeftIcon sx={{ fontSize: 17 }} />}
          </IconButton>
        </Box>
      )}

      {isMobile && (
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          PaperProps={{ sx: { width: EXPANDED, border: "none" } }}
        >
          <SidebarContent collapsed={false} pathname={location.pathname} onNavigate={navigate} />
        </Drawer>
      )}

      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {isMobile && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              borderBottom: `1px solid ${IPD_COLORS.border}`,
              bgcolor: IPD_COLORS.cardBg,
            }}
          >
            <IconButton onClick={() => setMobileOpen(true)} size="small" aria-label="Open the menu">
              <MenuIcon />
            </IconButton>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: IPD_COLORS.textPrimary }}>
              Emergency Department
            </Typography>
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0, borderRadius: `${IPD_SHAPE.radiusMd}px` }}>{children}</Box>
      </Box>
    </Box>
  );
}
